import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * A tiny "dial" pill you place next to any phone number in the app.
 *
 * On tap, it opens the number in the admin's chosen dialer app (system
 * dialer, Heyo Phone, Truecaller, or a custom `scheme://call/{number}`
 * the admin pasted in themselves). Preferences are stored per-user on
 * the server and cached in a module-level Promise so the first render
 * fetches once and every subsequent DialButton reuses it.
 *
 * The click handler is intentionally SYNCHRONOUS — Android Chrome only
 * honours custom-scheme navigations (`heyo://…`) while the browser is
 * still inside the original user-gesture stack. Awaiting an API call
 * before `window.location.href = url` causes Chrome to silently drop
 * the intent, which showed up as "clicking the icon does nothing".
 *
 * Usage:
 *   <span>{customer.phone} <DialButton number={customer.phone} /></span>
 */

const PRESETS = {
  system: "tel:{number}",
  // Heyo Phone (com.heyophone) doesn't publish a documented URL scheme, so
  // we use Android's `intent://` URI to explicitly target the Heyo package
  // and fall back to the system dialer if Heyo isn't installed. This
  // bypasses the OS "Open with" chooser entirely.
  heyo:
    "intent:{number}#Intent;scheme=tel;package=com.heyophone;S.browser_fallback_url=tel%3A{number};end",
  truecaller: "truecaller://call?type=call&number={number}",
};

const DEFAULT_PREFS = { preferred_dialer: "system", custom_dialer_scheme: "" };

// Module-level cache so the first render fetches, subsequent ones don't.
let prefsPromise = null;
let cachedPrefs = null; // resolved value, synchronously readable after first load

function loadPrefs() {
  if (!prefsPromise) {
    prefsPromise = api
      .get("/auth/preferences")
      .then((r) => {
        cachedPrefs = { ...DEFAULT_PREFS, ...(r.data || {}) };
        return cachedPrefs;
      })
      .catch(() => {
        cachedPrefs = DEFAULT_PREFS;
        return cachedPrefs;
      });
  }
  return prefsPromise;
}

// Called by the Settings page when the user updates their choice, so the
// next dial-button tap uses the fresh scheme without a page reload.
export function invalidateDialerPrefs() {
  prefsPromise = null;
  cachedPrefs = null;
}

function cleanNumber(raw) {
  const s = String(raw || "").trim();
  // Strip everything that isn't a digit or leading `+` — the tel: scheme
  // and most dialer apps don't tolerate spaces or dashes. We deliberately
  // do NOT URL-encode the number: Heyo & most Android VoIP schemes fail
  // to parse `%2B91…` and expect the raw `+91…` value.
  return s.replace(/(?!^\+)[^\d]/g, "");
}

function buildUrl(prefs, number) {
  let template = PRESETS[prefs?.preferred_dialer] || "tel:{number}";
  if (prefs?.preferred_dialer === "custom") {
    template = (prefs.custom_dialer_scheme || "").trim() || "tel:{number}";
  }
  if (!template.includes("{number}")) template = "tel:{number}";
  // Heyo's intent:// template contains two placeholders — one for the raw
  // number ("intent:+91…") and one already URL-encoded inside the
  // browser_fallback_url. Replace both, using the encoded form for any
  // placeholder that lives after "browser_fallback_url=tel%3A".
  const encoded = encodeURIComponent(number);
  return template.replace(/\{number\}/g, (_match, offset, str) => {
    const before = str.slice(Math.max(0, offset - 30), offset);
    return before.endsWith("tel%3A") ? encoded : number;
  });
}

export default function DialButton({
  number,
  className = "",
  size = "sm",
  label,
  mode = "device", // "device" opens the phone's dialer; "exotel" places a click-2-call via /api/calls/initiate
  taskId,
  callId,
}) {
  // Preload prefs on mount so every click is fully synchronous. We keep a
  // local snapshot so the button re-renders once prefs arrive.
  const [prefs, setPrefs] = useState(cachedPrefs);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (mode === "exotel") return; // device prefs unused for Exotel
    if (cachedPrefs) return; // already loaded
    let alive = true;
    loadPrefs().then((p) => {
      if (alive) setPrefs(p);
    });
    return () => {
      alive = false;
    };
  }, [mode]);

  const dial = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (mode === "exotel") {
      // Server-side click-to-call: your phone rings first, then Exotel
      // bridges the customer. Customer sees the company ExoPhone.
      // Employees NEVER see the raw number — we pass task_id / call_id and
      // the server resolves the real number, so this is also the primary
      // way to keep call recording + audit trail intact.
      if (placing) return;
      if (!taskId && !callId) {
        toast.error("Missing task/call context — reload and try again.");
        return;
      }
      setPlacing(true);
      const payload = taskId ? { task_id: taskId } : { call_id: callId };
      api
        .post("/calls/initiate", payload)
        .then(() => {
          toast.success("Ringing your phone — pick up to connect the customer.");
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail || "Could not place the call.");
        })
        .finally(() => setPlacing(false));
      return;
    }

    const n = cleanNumber(number);
    if (!n) {
      toast.error("No phone number to dial.");
      return;
    }
    // If prefs haven't loaded yet, fall back to system dialer NOW instead
    // of awaiting — losing the gesture context would prevent Android from
    // opening the custom-scheme app either way. Next click will use the
    // real prefs once they've arrived.
    const url = buildUrl(prefs || cachedPrefs || DEFAULT_PREFS, n);
    window.location.href = url;
  };

  const sizeCls = size === "xs"
    ? "w-6 h-6 p-0"
    : size === "lg"
      ? "h-9 px-3"
      : "w-7 h-7 p-0";

  // Exotel-mode pill is coloured indigo so employees/admins immediately
  // recognise it as the recorded, company-routed dial button.
  const paletteCls =
    mode === "exotel"
      ? "bg-indigo-600 text-white hover:bg-indigo-700"
      : "bg-emerald-600 text-white hover:bg-emerald-700";

  return (
    <button
      type="button"
      onClick={dial}
      disabled={(!number && mode !== "exotel") || placing}
      className={`inline-flex items-center justify-center rounded-full disabled:opacity-50 transition-colors ${paletteCls} ${sizeCls} ${className}`}
      title={
        mode === "exotel"
          ? "Call via Exotel — your phone rings first"
          : label || `Call ${number}`
      }
      aria-label={
        mode === "exotel"
          ? "Call via Exotel"
          : label || `Call ${number}`
      }
      data-testid={
        mode === "exotel"
          ? `dial-exotel-${taskId || callId || ""}`
          : `dial-btn-${cleanNumber(number)}`
      }
    >
      <Phone className={size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      {size === "lg" && <span className="ml-1.5 text-xs mono">{label || (mode === "exotel" ? "Exotel call" : "Call")}</span>}
    </button>
  );
}
